import { z } from 'zod'

import { redactWriteOnlyCredentials } from './secrets'

// Integration providers
export const INTEGRATION_PROVIDERS = [
  'disputifier',
  'shopify',
  'woocommerce',
  'facebook',
  'tiktok',
  'klaviyo',
  'converge',
  'sendcloud',
  'bridge',
  'klarna',
  'paypal',
  'apple_pay',
  'google_pay',
  'meta_conversion',
  'awin_conversion',
  'gtm',
  'clarity',
  'snapchat',
  'applovin',
  'triplewhale',
  'oceanpayment',
  'webhooks',
  'whop',
  'whop_platforms',
  'slash',
  'convesiopay',
  'hipay',
  'tabby',
  'chargeblast',
  'airwallex_apm',
  'stripe_apm',
  'tagadapay_apm',
  'rcvr',
  'xcaliber',
  'finix',
  'zelle',
  'custom_payment',
  'crypto',
  'taxjar',
  'wetracked',
  'clickflare',
  'voluum',
  'binom',
  'redtrack',
  'clickmagick',
  'hyros',
  'platformpay',
] as const

export const IntegrationProviderSchema = z.string()

// Integration types
export const INTEGRATION_TYPES = [
  'storefront',
  'payment',
  'ads_tracking',
  'analytics',
  'pixel',
  'checkout_pickup_point',
  'charge_back',
  'tax',
  'post_purchase_hook',
  // Tagada Rx rows use this type (`integrationRx.createActivationRequest`).
  // Missing it makes integrations.list Zod-fail for the whole store and the
  // CRM shows Shopify/PayPal as disconnected even when credentials are valid.
  'telehealth',
] as const

export const IntegrationTypeSchema = z.enum(INTEGRATION_TYPES)

// Basic integration schema
export const IntegrationSchema = z.object({
  id: z.string(),
  storeId: z.string(),
  provider: IntegrationProviderSchema,
  type: IntegrationTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  enabled: z.boolean(),
  accountId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

// Integration with credentials (for internal use)
export const IntegrationWithCredentialsSchema = IntegrationSchema.extend({
  credentials: z.record(z.string(), z.any()).nullable(),
  settings: z.record(z.string(), z.any()).nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
})

// Integration list item (optimized for lists/tables)
export const IntegrationListItemSchema = z.object({
  id: z.string(),
  storeId: z.string(),
  provider: IntegrationProviderSchema,
  type: IntegrationTypeSchema,
  name: z.string(),
  logoUrl: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: z.date(),
})

// Integration details (for detail views)
export const IntegrationDetailsSchema = z.object({
  id: z.string(),
  storeId: z.string(),
  provider: IntegrationProviderSchema,
  type: IntegrationTypeSchema,
  name: z.string(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  enabled: z.boolean(),
  credentials: z.record(z.string(), z.any()).nullable(),
  settings: z.record(z.string(), z.any()).nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
  accountId: z.string(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

// Integration with store relation
export const IntegrationWithStoreSchema = IntegrationSchema.extend({
  store: z.object({
    id: z.string(),
    name: z.string(),
    type: z.string(),
  }),
})

// Specific credential schemas
export const ShopifyCredentialsSchema = z.object({
  shopUrl: z.string().min(1),
  apiToken: z.string().min(1),
  apiKey: z.string().min(1).optional(),
  apiSecretKey: z.string().min(1).optional(),
  syncOrders: z.boolean().default(true),
  syncPresentmentCurrencies: z.boolean().default(true),
  sendReceipt: z.boolean().default(true),
  sendRebillReceipt: z.boolean().default(false),
  sendRebillFulfillmentReceipt: z.boolean().default(false),
  sendFulfillmentReceipt: z.boolean().default(false),
  dynamicDiscountEnabled: z.boolean().default(false),
  dynamicDiscountStrategy: z.enum(['order-level', 'line-item-allocation']).default('order-level'),
  dynamicSellingPlanEnabled: z.boolean().default(false),
  dynamicPriceSet: z.boolean().default(false),
  autoSyncEnabled: z.boolean().default(false),
  autoSyncFrequency: z.number().default(15),
  enableWetracked: z.boolean().default(false),
})

export const WoocommerceCredentialsSchema = z.object({
  url: z.string().startsWith('http'),
  consumerKey: z.string().min(1).nullable(),
  consumerSecret: z.string().min(1).nullable(),
  syncOrdersType: z.enum(['pending', 'processing', 'completed']).default('processing'),
  pushOrder: z.boolean().default(true),
  pushRebillOrders: z.boolean().default(true),
  autoSyncEnabled: z.boolean().default(false),
})

export const PaypalCredentialsSchema = z.object({
  sandboxed: z.boolean(),
  clientId: z.string(),
  clientSecret: z.string(),
})

export const SendCloudCredentialsSchema = z.object({
  apiToken: z.string(),
  carriers: z
    .array(
      z.object({
        value: z.string(),
        name: z.string(),
      }),
    )
    .min(1),
})

export const BridgeCredentialsSchema = z.object({
  clientId: z.string().min(1).trim(),
  clientSecret: z.string().min(1).trim(),
})

export const KlarnaCredentialsSchema = z.object({
  apiKey: z.string().min(1).trim(),
  sandboxed: z.boolean().optional().default(false),
  location: z.enum(['North America', 'Europe', 'Oceania']).default('North America'),
})

export const ApplePayCredentialsSchema = z.object({
  hasVerifiedDomain: z.boolean().optional().default(false),
  domain: z.string().optional(),
  sandboxed: z.boolean().optional().default(false),
})

export const GooglePayCredentialsSchema = z.object({
  merchantName: z.string().min(1, 'Merchant name is required'),
  merchantId: z.string().min(1, 'Merchant ID is required'),
  sandboxed: z.boolean().optional().default(false),
  enableExpressCheckout: z.boolean().optional().default(true),
  enablePaymentFormCheckout: z.boolean().optional().default(false),
  enableBuyNowPayLater: z.boolean().optional().default(false),
  enableStrongCustomerAuthentication: z.boolean().optional().default(false),
})

// Type exports
export type IntegrationProvider = (typeof INTEGRATION_PROVIDERS)[number] | (string & {})
export type IntegrationType = z.infer<typeof IntegrationTypeSchema>
export type Integration = z.infer<typeof IntegrationSchema>
export type IntegrationWithCredentials = z.infer<typeof IntegrationWithCredentialsSchema>
export type IntegrationListItem = z.infer<typeof IntegrationListItemSchema>
export type IntegrationDetails = z.infer<typeof IntegrationDetailsSchema>
export type IntegrationWithStore = z.infer<typeof IntegrationWithStoreSchema>
export type ShopifyCredentials = z.infer<typeof ShopifyCredentialsSchema>
export type WoocommerceCredentials = z.infer<typeof WoocommerceCredentialsSchema>
export type PaypalCredentials = z.infer<typeof PaypalCredentialsSchema>
export type SendCloudCredentials = z.infer<typeof SendCloudCredentialsSchema>
export type BridgeCredentials = z.infer<typeof BridgeCredentialsSchema>
export type KlarnaCredentials = z.infer<typeof KlarnaCredentialsSchema>
export type ApplePayCredentials = z.infer<typeof ApplePayCredentialsSchema>
export type GooglePayCredentials = z.infer<typeof GooglePayCredentialsSchema>

// DTO converters
export const IntegrationDto = (object: unknown) => IntegrationSchema.parse(object)
export const IntegrationWithCredentialsDto = (object: unknown) =>
  redactWriteOnlyCredentials(IntegrationWithCredentialsSchema.parse(object))
export const IntegrationListItemDto = (object: unknown) => IntegrationListItemSchema.parse(object)
export const IntegrationDetailsDto = (object: unknown) => redactWriteOnlyCredentials(IntegrationDetailsSchema.parse(object))
export const IntegrationWithStoreDto = (object: unknown) => IntegrationWithStoreSchema.parse(object)

